// StepSelectParameter.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Box,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Stack,
  Collapse,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import Label from "../../../components/Label/Label";
import { useDispatch, useSelector } from "react-redux";
import { getParametersList } from "../../../redux/reports/actions";
import { limitTypeOptions } from "../visualization/constants";

const specLimitTypeOptions = ["User Preset", "User Defined"];
const getEmptyConfig = () => ({
  selected: false,
  specLimitType: "",
  specLimitSetId: "",
  lowerSpecLimit: "",
  upperSpecLimit: "",
  specificationTarget: "",
  controlLimitType: "Sample Calculation",
  controlLimitSetId: "",
  lowerControlLimit: "",
  upperControlLimit: "",
});

const ParameterRow = React.memo(
  ({ row, config, errors, onCheckboxChange, onConfigChange, onClear }) => {
    const id = row.parameter_id;
    const hasSpecSets = (row.spec_limit_sets || []).length > 0;
    const hasControlSets = (row.control_limit_sets || []).length > 0;

    return (
      <TableRow key={id} hover>
        <TableCell padding="checkbox" sx={{ width: 60 }}>
          <Checkbox
            size="small"
            checked={!!config.selected}
            onChange={() => onCheckboxChange(id)}
          />
        </TableCell>

        <TableCell sx={{ width: 180 }}>
          <Label text={row.parameter_name} />
        </TableCell>
        <TableCell sx={{ width: 160 }}>
          <Label text={row.product_name} />
        </TableCell>
        <TableCell sx={{ width: 160 }}>
          <Label text={row.site_name} />
        </TableCell>
        <TableCell sx={{ width: 160 }}>
          <Label text={row.unit_op_name} />
        </TableCell>

        {/* Specification Limit */}
        <TableCell sx={{ p: 1, width: 320, verticalAlign: "top" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl fullWidth size="small" error={!!errors.specLimitSetId}>
              <Select
                displayEmpty
                value={config.specLimitType}
                onChange={(e) =>
                  onConfigChange(id, "specLimitType", e.target.value)
                }
              >
                <MenuItem disabled value="">
                  <em>
                    <Label text="Select Limit" />
                  </em>
                </MenuItem>
                {specLimitTypeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    <Label text={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* User Preset */}
            {config.specLimitType === "User Preset" && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.specLimitSetId}
              >
                <Select
                  value={config.specLimitSetId}
                  onChange={(e) =>
                    onConfigChange(id, "specLimitSetId", e.target.value)
                  }
                >
                  {hasSpecSets ? (
                    row.spec_limit_sets.map((site, idx) => (
                      <MenuItem
                        key={`${id}-spec-${site.spec_limit_id}-${idx}`}
                        value={site.spec_limit_id}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Label text={site.set_id} />
                          <Tooltip
                            arrow
                            title={
                              <Box
                                sx={{
                                  p: 1,
                                  background: "#fff",
                                  borderRadius: 1,
                                  minWidth: 180,
                                }}
                              >
                                <Label text="Specification" fontWeight="500" />
                                <Divider sx={{ my: 0.5 }} />
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "60px 1fr",
                                    gap: 0.5,
                                  }}
                                >
                                  <Label text="LSL:" fontWeight="500" />
                                  <Label text={site.LSL ?? "N/A"} />
                                  <Label text="USL:" fontWeight="500" />
                                  <Label text={site.USL ?? "N/A"} />
                                  <Label text="Target:" fontWeight="500" />
                                  <Label text={site.Target ?? "N/A"} />
                                </Box>
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoOutlinedIcon
                                fontSize="small"
                                color="primary"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Label text="No sets available" />
                    </MenuItem>
                  )}
                </Select>
                {errors.specLimitSetId && (
                  <FormHelperText>
                    <Label text={errors.specLimitSetId} color="inherit" />
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {/* User Defined */}
            {config.specLimitType === "User Defined" && (
              <>
                <Stack direction="row" spacing={1}>
                  {[
                    { label: "LSL", field: "lowerSpecLimit" },
                    { label: "USL", field: "upperSpecLimit" },
                    { label: "Target", field: "specificationTarget" },
                  ].map((item) => (
                    <TextField
                      key={item.field}
                      size="small"
                      type="number"
                      placeholder={
                        item.label === "LSL"
                          ? `LSL*`
                          : item.label === "USL"
                            ? "USL*"
                            : item.label
                      }
                      value={config[item.field] || ""}
                      onChange={(e) =>
                        onConfigChange(id, item.field, e.target.value)
                      }
                      sx={{ width: 90 }}
                    />
                  ))}
                </Stack>
                {errors.userDefinedSpec && (
                  <FormHelperText error sx={{ marginTop: "0px" }}>
                    <Label text={errors.userDefinedSpec} color="inherit" />
                  </FormHelperText>
                )}
              </>
            )}
          </Box>
        </TableCell>

        {/* Control Limit */}
        <TableCell sx={{ p: 1, width: 320, verticalAlign: "top" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.controlLimitSetId}
            >
              <Select
                displayEmpty
                value={config.controlLimitType}
                onChange={(e) =>
                  onConfigChange(id, "controlLimitType", e.target.value)
                }
              >
                <MenuItem disabled value="">
                  <em>Select Limit</em>
                </MenuItem>
                {limitTypeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    <Label text={opt} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* User Preset */}
            {config.controlLimitType === "User Preset" && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.controlLimitSetId}
              >
                <Select
                  value={config.controlLimitSetId}
                  onChange={(e) =>
                    onConfigChange(id, "controlLimitSetId", e.target.value)
                  }
                >
                  {hasControlSets ? (
                    row.control_limit_sets.map((site, idx) => (
                      <MenuItem
                        key={`${id}-ctrl-${site.stat_limit_id}-${idx}`}
                        value={site.stat_limit_id}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Label text={site.set_id} />
                          <Tooltip
                            arrow
                            title={
                              <Box
                                sx={{
                                  p: 1,
                                  background: "#fff",
                                  borderRadius: 1,
                                  minWidth: 180,
                                }}
                              >
                                <Label text="Control" fontWeight="500" />
                                <Divider sx={{ my: 0.5 }} />
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "60px 1fr",
                                    gap: 0.5,
                                  }}
                                >
                                  <Label text="UCL:" fontWeight="500" />
                                  <Label text={site.UCL ?? "N/A"} />
                                  <Label text="LCL:" fontWeight="500" />
                                  <Label text={site.LCL ?? "N/A"} />
                                </Box>
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoOutlinedIcon
                                fontSize="small"
                                color="primary"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Label text="No sets available" />
                    </MenuItem>
                  )}
                </Select>
                {errors.controlLimitSetId && (
                  <FormHelperText>
                    <Label text={errors.controlLimitSetId} color="inherit" />
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {/* User Defined */}
            {config.controlLimitType === "User Defined" && (
              <>
                <Stack direction="row" spacing={1}>
                  {[
                    { label: "LCL", field: "lowerControlLimit" },
                    { label: "UCL", field: "upperControlLimit" },
                  ].map((item) => (
                    <TextField
                      key={item.field}
                      size="small"
                      type="number"
                      placeholder={`${item.label}*`}
                      value={config[item.field] || ""}
                      onChange={(e) =>
                        onConfigChange(id, item.field, e.target.value)
                      }
                      sx={{ width: 120 }}
                    />
                  ))}
                </Stack>
                {errors.userDefinedControl && (
                  <FormHelperText error sx={{ marginTop: "0px" }}>
                    <Label text={errors.userDefinedControl} color="inherit" />
                  </FormHelperText>
                )}
              </>
            )}
          </Box>
        </TableCell>

        <TableCell padding="checkbox" sx={{ width: 60 }}>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={() => onClear(id)} color="error">
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  }
);

// ---------------- Main ----------------
const StepSelectParameter = ({
  formData,
  prefilledParameters = [],
  onChange,
  onValidate,
}) => {
  const dispatch = useDispatch();
  const { data: parameterData = [], loader: parameterLoader } = useSelector(
    (state) => state.reports.parameterList || {}
  );

  const selectedProductId = formData?.product?.product_id || "";
  const selectedSiteId = formData?.product?.site_id || "";

  const [rowConfigs, setRowConfigs] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const initializedRef = useRef(false);

  useEffect(() => {
    if (selectedProductId && selectedSiteId) {
      dispatch(
        getParametersList({
          drug_product_id: selectedProductId,
          site_id: selectedSiteId,
        })
      );
    }
  }, [dispatch, selectedProductId, selectedSiteId]);

  // initialize configs once
  useEffect(() => {
    if (parameterData.length && !initializedRef.current) {
      const initialConfigs = parameterData.reduce((acc, row) => {
        const prefill = prefilledParameters.find(
          (p) => p.parameter_id === row.parameter_id
        );
        acc[row.parameter_id] = prefill?.config
          ? { ...getEmptyConfig(), ...prefill.config, selected: true }
          : getEmptyConfig();
        return acc;
      }, {});
      setRowConfigs(initialConfigs);
      setRowErrors({});
      initializedRef.current = true;
    }
  }, [parameterData, prefilledParameters]);

  // Notify validation status
  useEffect(() => {
    const hasErrors = Object.values(rowErrors).some(
      (errs) => errs && Object.keys(errs).length > 0
    );
    onValidate?.(!hasErrors);
  }, [rowErrors, onValidate]);

  const validateRow = useCallback((rowId, config) => {
    const errors = {};
    // if (config.specLimitType === "User Preset" && !config.specLimitSetId)
    //   errors.specLimitSetId = "Please select a specification set";
    // if (
    //   config.specLimitType === "User Defined" &&
    //   !(config.lowerSpecLimit && config.upperSpecLimit)
    //   //   ||
    //   // !config.specificationTarget
    // )
    //   errors.userDefinedSpec = "Enter the lower and upper spec limit values";
    if (config.controlLimitType === "User Preset" && !config.controlLimitSetId)
      errors.controlLimitSetId = "Please select a control set";
    if (
      config.controlLimitType === "User Defined" &&
      !(config.lowerControlLimit && config.upperControlLimit)
    )
      errors.userDefinedControl =
        "Enter the lower and upper control limit values.";
    return errors;
  }, []);

  const updateRow = useCallback(
    (rowId, changes) => {
      setRowConfigs((prev) => {
        const updated = { ...prev, [rowId]: { ...prev[rowId], ...changes } };
        onChange?.(
          parameterData
            .filter((r) => updated[r.parameter_id]?.selected)
            .map((r) => ({ ...r, config: updated[r.parameter_id] }))
        );
        setRowErrors((prevErrs) => ({
          ...prevErrs,
          [rowId]: validateRow(rowId, updated[rowId]),
        }));
        return updated;
      });
    },
    [onChange, parameterData, validateRow]
  );

  const handleCheckboxChange = useCallback(
    (rowId) => updateRow(rowId, { selected: !rowConfigs[rowId]?.selected }),
    [updateRow, rowConfigs]
  );
  const handleConfigChange = useCallback(
    (rowId, field, value) => updateRow(rowId, { [field]: value }),
    [updateRow]
  );
  const handleClearRow = useCallback(
    (rowId) => updateRow(rowId, getEmptyConfig()),
    [updateRow]
  );

  const rows = useMemo(
    () =>
      parameterData.map((row) => (
        <ParameterRow
          key={row.parameter_id}
          row={row}
          config={rowConfigs[row.parameter_id] || getEmptyConfig()}
          errors={rowErrors[row.parameter_id] || {}}
          onCheckboxChange={handleCheckboxChange}
          onConfigChange={handleConfigChange}
          onClear={handleClearRow}
        />
      )),
    [
      parameterData,
      rowConfigs,
      rowErrors,
      handleCheckboxChange,
      handleConfigChange,
      handleClearRow,
    ]
  );

  if (parameterLoader)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );
  if (!parameterData?.length)
    return (
      <Label text="No parameters available." fontSize="14px" fontWeight="600" />
    );

  return (
    <Box>
      <Label
        text="Select Parameters"
        fontWeight="700"
        fontSize="14px"
        paddingBottom={"8px"}
      />

      <TableContainer sx={{ height: "calc(100vh - 340px)",padding:"0px" }}>
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#E9E9E9",
                height: 44,
                "& th": {
                  padding: "8px",
                  lineHeight: "normal",
                  backgroundColor: "#E9E9E9",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableCell sx={{ width: 60 }}>
                <Label text="Select" />
              </TableCell>
              <TableCell sx={{ width: 250 }}>
                <Label text="Parameter Name" />
              </TableCell>
              <TableCell sx={{ width: 180 }}>
                <Label text="Parameter Product" />
              </TableCell>
              <TableCell sx={{ width: 180 }}>
                <Label text="Manufacturing Site" />
              </TableCell>
              <TableCell sx={{ width: 200 }}>
                <Label text="Unit Operation" />
              </TableCell>
              <TableCell sx={{ width: 290 }}>
                <Label text="Specification Limit" />
              </TableCell>
              <TableCell sx={{ width: 200 }}>
                <Label text="Control Limit" />
              </TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StepSelectParameter;
